import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {tap} from "rxjs/operators";
import {LicenseModel} from "../../../models/license.model";
import {LicenseService} from "../../../services/licenses-service/license.service";

@Component({
    selector: 'app-licenses-licenses-list-card',
    templateUrl: './licenses-licenses-list-card.component.html',
    styleUrls: ['./licenses-licenses-list-card.component.scss'],
    standalone: false
})
export class LicensesLicensesListCardComponent implements OnInit {

  licenses: LicenseModel[];
  filteredLicenses: LicenseModel[] = [];
  showFinished = false;
  filterQuery = '';
  sortField: 'description' | 'companyName' | 'startDateMillis' | 'endDateMillis' | 'quantity' = 'endDateMillis';
  sortDirection: 'asc' | 'desc' = 'asc';
  pendingDeleteLicense: LicenseModel = null;

  constructor(
    private licenseService: LicenseService,
    private destroyRef: DestroyRef
  ) { }

  ngOnInit() {
    this.listenRefreshLicenses();
    this.getLicenses();
  }

  public getLicenses() {
    this.licenseService.getLicenses().pipe(
      takeUntilDestroyed(this.destroyRef),
      tap((res: LicenseModel[]) => {
        this.licenses = res;
        this.applyFilter();
      })
    ).subscribe();
  }

  public isExpired(millis: number) {
    const endDate = this.toMillis(millis);
    return new Date().getTime() > endDate;
  }

  public isExpiringSoon(millis: number) {
    const endDate = this.toMillis(millis);
    const now = new Date().getTime();
    const days14InMillis = 14 * 24 * 60 * 60 * 1000;
    return endDate > now && endDate <= (now + days14InMillis);
  }

  public onShowFinishedChange(checked: boolean) {
    this.showFinished = checked;
    this.applyFilter();
  }

  public onFilterQueryChange(query: string) {
    this.filterQuery = query ?? '';
    this.applyFilter();
  }

  public exportCsv() {
    if (!this.filteredLicenses || this.filteredLicenses.length === 0) {
      return;
    }

    const headers = [
      'Activo',
      'Nombre',
      'Compañía',
      'Fecha inicio',
      'Fecha fin',
      'Disponibles',
      'Usadas',
      'Centro',
      'Aplicación'
    ];

    const rows = this.filteredLicenses.map((license: LicenseModel) => [
      license.active ? 'Sí' : 'No',
      license.description || '',
      license.companyName || '',
      this.formatDate(license.startDateMillis),
      this.formatDate(license.endDateMillis),
      license.quantity > 0 ? `${license.quantity}` : 'Ilimitadas',
      `${license.inUse ?? ''}`,
      license.centerName || 'Todos',
      license.applicationName || 'Todas'
    ]);

    const csv = [headers, ...rows]
      .map((row: string[]) => row.map((value: string) => this.escapeCsv(value)).join(','))
      .join('\n');

    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'licenses.csv';
    link.click();
    URL.revokeObjectURL(url);
  }

  public setSort(field: 'description' | 'companyName' | 'startDateMillis' | 'endDateMillis' | 'quantity') {
    if (this.sortField === field) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDirection = 'asc';
    }
    this.applySort();
  }

  public sortIndicator(field: 'description' | 'companyName' | 'startDateMillis' | 'endDateMillis' | 'quantity') {
    if (this.sortField !== field) {
      return '↕';
    }
    return this.sortDirection === 'asc' ? '▲' : '▼';
  }

  public deleteLicense(license) {
    this.pendingDeleteLicense = license;
  }

  public cancelDeleteLicense() {
    this.pendingDeleteLicense = null;
  }

  public confirmDeleteLicense() {
    if (!this.pendingDeleteLicense) {
      return;
    }
    this.licenseService.deleteLicense(this.pendingDeleteLicense.internalId).pipe(
      takeUntilDestroyed(this.destroyRef),
      tap(res => this.licenseService.refreshLicenses())
    ).subscribe();
    this.pendingDeleteLicense = null;
  }

  private listenRefreshLicenses() {
    this.licenseService.listenRefreshLicenses().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(
      (res) => {
        this.getLicenses();
      }
    );
  }

  private applyFilter() {
    if (!this.licenses) {
      this.filteredLicenses = [];
      return;
    }
    const tokens = this.normalize(this.filterQuery).split(/\s+/).filter((token: string) => token.length > 0);
    this.filteredLicenses = this.licenses.filter((license: LicenseModel) => {
      const expired = this.isExpired(license.endDateMillis);
      const matchesExpiration = this.showFinished || !expired;
      if (!matchesExpiration) {
        return false;
      }
      if (tokens.length === 0) {
        return true;
      }
      const haystack = this.normalize([
        license.description,
        license.companyName,
        license.centerName,
        license.applicationName
      ].filter((value: string) => !!value).join(' '));
      return tokens.every((token: string) => haystack.includes(token));
    });
    this.applySort();
  }

  private applySort() {
    const factor = this.sortDirection === 'asc' ? 1 : -1;
    this.filteredLicenses = [...this.filteredLicenses].sort((a: LicenseModel, b: LicenseModel) => {
      let left: any = (a as any)[this.sortField];
      let right: any = (b as any)[this.sortField];

      if (this.sortField === 'description' || this.sortField === 'companyName') {
        left = (left ?? '').toString().toLowerCase();
        right = (right ?? '').toString().toLowerCase();
        return left.localeCompare(right) * factor;
      }

      if (this.sortField === 'quantity') {
        // Treat non-positive quantity as unlimited, pushing it to the end on asc.
        left = left > 0 ? left : Number.MAX_SAFE_INTEGER;
        right = right > 0 ? right : Number.MAX_SAFE_INTEGER;
      }

      return ((left ?? 0) - (right ?? 0)) * factor;
    });
  }

  private toMillis(value: number) {
    if (!value) {
      return 0;
    }
    // Accept both Unix seconds and milliseconds from backend payloads.
    return value < 1000000000000 ? value * 1000 : value;
  }

  private normalize(value: string) {
    return (value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  private formatDate(value: number) {
    const millis = this.toMillis(value);
    if (!millis) {
      return '';
    }
    const date = new Date(millis);
    const day = `${date.getDate()}`.padStart(2, '0');
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  private escapeCsv(value: string) {
    const safeValue = (value ?? '').toString();
    return `"${safeValue.replace(/"/g, '""')}"`;
  }

}
